// 模板成员仅引用现有规则，整体保存保留绑定身份（D122、D126、docs/10 §10.13）。
import { rowKey } from "@/app/form";
import type { EntityStatus } from "@/app/master";
import type { RoutingRule, RoutingTemplate, RoutingTemplateInput } from "@/app/routing";
import type { Errors } from "@/app/useDraft";
import { routingOrderError } from "./routingBindingValidation";

/** 输入行用稳定 key 保留控件状态，id 只用于服务端绑定身份。 */
export interface TemplateItemDraft { key: number; id: number; rule_id: string; priority: string }
/** 草稿捕获对象版本。 */
export interface RoutingTemplateDraft { name: string; note: string; status: EntityStatus; version: number; items: TemplateItemDraft[] }

/** 新增成员不猜测优先级，不静默调整现有匹配顺序。 */
export function newTemplateItem(): TemplateItemDraft { return { key: rowKey(), id: 0, rule_id: "", priority: "" }; }

/** 初始显示顺序按小数字优先，绑定 ID 不随排序改变。 */
export function routingTemplateDraft(row?: RoutingTemplate): RoutingTemplateDraft {
  return { name: row?.name ?? "", note: row?.note ?? "", status: row?.status ?? "active", version: row?.version ?? 0,
     items: row ? [...row.items].sort((a, b) => a.priority - b.priority)
      .map(b => ({ key: rowKey(), id: b.id, rule_id: String(b.rule_id), priority: String(b.priority) })) : [newTemplateItem()] };
}

/** 校验引用、整数优先级和兜底位置，服务器仍完整校验共享图；规则不能指向模板，因此不会嵌套（D131）。 */
export function routingTemplateInput(d: RoutingTemplateDraft, rules: RoutingRule[]): { body?: RoutingTemplateInput; errors: Errors<RoutingTemplateDraft> } {
  const errors: Errors<RoutingTemplateDraft> = {};
  if (!d.name.trim()) errors.name = "填写模板名称。";
  if (!d.items.length) errors.items = "模板至少需要一条规则。";
  const items = d.items.map(b => ({ id: b.id, rule_id: Number(b.rule_id), priority: Number(b.priority) }));
  for (let i = 0; i < items.length; i++) {
    const b = items[i];
    const r = rules.find(r => r.id === b.rule_id);
    if (!r) { errors.items = `第 ${i + 1} 条请选择已有路由规则。`; break; }
  }
  const ordered = [...items].sort((a, b) => a.priority - b.priority);
  if (!errors.items) {
    const orderError = routingOrderError(d.items.map(b => ({ priority: b.priority, match: rules.find(r => r.id === Number(b.rule_id))!.match })));
    if (orderError) errors.items = orderError === "优先级不能重复。" ? "模板内优先级不能重复。" : orderError;
  }
  if (Object.keys(errors).length) return { errors };
  return { errors, body: { name: d.name.trim(), note: d.note.trim() || null, status: d.status, version: d.version, items: ordered } };
}
